import { useEffect, useState } from "react"
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material"
import { collection, query, where, onSnapshot } from "firebase/firestore"
import { db } from "../../firebase"
import { tokens } from "../../theme"
import { useNavigate } from "react-router-dom"
import AddIcon from "@mui/icons-material/Add"
import dayjs from "dayjs"
import { buildWeekId, getIsoWeekInfo, getIsoWeekDateRange, getIsoWeeksInYear } from "../../utils/isoWeek"
import { IWeeklyHygieneManagement, formatWeekRangeLabel } from "./lib/weeklyHygieneManagement"
import { IBusinessHoliday, isRangeFullyHoliday } from "../../utils/businessHolidays"

const WeeklyHygieneManagement = () => {
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const navigate = useNavigate()
  const [records, setRecords] = useState<Record<number, IWeeklyHygieneManagement>>({})
  const [holidays, setHolidays] = useState<IBusinessHoliday[]>([])
  const [selectedYear, setSelectedYear] = useState<number>(getIsoWeekInfo(new Date()).isoYear)

  const weeksInSelectedYear = getIsoWeeksInYear(selectedYear)
  const currentIsoWeek = getIsoWeekInfo(new Date())

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "businessHolidays"), (snapshot) => {
      setHolidays(snapshot.docs.map(d => ({ id: d.id, ...d.data() } as IBusinessHoliday)))
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    const hygieneManagementsRef = collection(db, "weeklyHygieneManagements")
    const q = query(hygieneManagementsRef, where("year", "==", selectedYear.toString()))

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const recordsMap: Record<number, IWeeklyHygieneManagement> = {}
      snapshot.docs.forEach(doc => {
        const data = doc.data() as IWeeklyHygieneManagement
        if (!data.weekOfYear) return
        recordsMap[data.weekOfYear] = {
          id: doc.id,
          ...data
        }
      })
      setRecords(recordsMap)
    })

    return unsubscribe
  }, [selectedYear])

  const handleOpen = (weekOfYear: number) => {
    navigate(`/admin/weekly-hygiene-management/${buildWeekId(selectedYear, weekOfYear)}`)
  }

  return (
    <Box m={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          衛生管理点検票（週次）
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>年</InputLabel>
            <Select
              value={selectedYear}
              label="年"
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {Array.from({ length: 5 }, (_, i) => dayjs().year() - 2 + i).map((year) => (
                <MenuItem key={year} value={year}>
                  {year}年
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: colors.blueAccent[700] }}>
              <TableCell sx={{ fontWeight: "bold" }}>週</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>期間</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>状態</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>衛生管理者</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>担当責任者</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>確認者</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>更新日時</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: weeksInSelectedYear }, (_, i) => i + 1).map((weekOfYear) => {
              const record = records[weekOfYear]
              const hasData = !!record
              const { start, end } = getIsoWeekDateRange(selectedYear, weekOfYear)
              const isCurrentWeek =
                selectedYear === currentIsoWeek.isoYear && weekOfYear === currentIsoWeek.isoWeek
              const isHoliday = !hasData && isRangeFullyHoliday(start, end, holidays)

              return (
                <TableRow
                  key={weekOfYear}
                  hover
                  selected={isCurrentWeek}
                >
                  <TableCell>第{weekOfYear}週</TableCell>
                  <TableCell>{formatWeekRangeLabel(start, end)}</TableCell>
                  <TableCell>
                    {isHoliday ? (
                      <Chip label="休業" color="default" size="small" />
                    ) : hasData ? (
                      <Chip label="入力済" color="success" size="small" />
                    ) : (
                      <Chip label="未入力" color="warning" size="small" />
                    )}
                  </TableCell>
                  <TableCell>{record?.manager || "-"}</TableCell>
                  <TableCell>{record?.supervisor || "-"}</TableCell>
                  <TableCell>{record?.verifier || "-"}</TableCell>
                  <TableCell>{record?.updatedAt || "-"}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      {isHoliday ? (
                        <Typography variant="body2" color="textSecondary">
                          休業のため対象外
                        </Typography>
                      ) : hasData ? (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleOpen(weekOfYear)}
                        >
                          編集
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          color="primary"
                          startIcon={<AddIcon />}
                          onClick={() => handleOpen(weekOfYear)}
                        >
                          入力
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default WeeklyHygieneManagement
